// src/components/enduser/subscription/FreeTrialOfferPopup.tsx
// קומפוננטה משותפת: פופאפ הצעת "ניסיון חינם" (מאי 2026).
// מוצג פעם אחת למשתמש זכאי שטרם ראה - בדשבורד (ImportSelectionPage) ובדף בחירת המנוי (SubscriptionPage).
// מקור אמת יחיד ללוגיקת הזכאות, דגל ה"נראה" וה-JSX - כדי שהדגל ישותף בין הדפים
// ושהפופאפ יוצג פעם אחת בלבד למשתמש, לא פעם אחת לכל דף.

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEnduserAuth } from "../../../contexts/EnduserAuthContext";
import { useMotionSafe } from "../../../hooks/useMotionSafe";

const FREE_TRIAL_OFFER_SEEN_KEY = "cartax_free_trial_offer_seen";

const FreeTrialOfferPopup: React.FC = () => {
  const { user, subscriptionStatus, canAccessCalculators } = useEnduserAuth();
  const navigate = useNavigate();
  const { getHoverClass } = useMotionSafe();

  const [showFreeTrialPopup, setShowFreeTrialPopup] = useState(false);

  // זכאי לניסיון: ללא גישה חופשית, אינו יכול לחשב, אין מנוי, טרם הפעיל ניסיון, ו-0 ניצול.
  const isEligibleForFreeTrial =
    !user?.free_access &&
    !canAccessCalculators() &&
    subscriptionStatus?.type === "no_subscription" &&
    !subscriptionStatus?.free_trial_activated &&
    (subscriptionStatus?.free_trial_used ?? 0) === 0;

  // המפתח ב-localStorage לפי משתמש - כדי שדגל ה"נראה" לא ידלוף בין חשבונות במכשיר משותף.
  const offerSeenKey = user?.id
    ? `${FREE_TRIAL_OFFER_SEEN_KEY}_${user.id}`
    : FREE_TRIAL_OFFER_SEEN_KEY;

  const hasSeenOffer = (): boolean => {
    try {
      return localStorage.getItem(offerSeenKey) === "1";
    } catch {
      return false;
    }
  };

  const markOfferSeen = () => {
    try {
      localStorage.setItem(offerSeenKey, "1");
    } catch {
      /* localStorage חסום - מתעלמים, הפופאפ פשוט עלול לחזור */
    }
  };

  // הצגת הפופאפ פעם אחת למשתמש זכאי שטרם ראה.
  useEffect(() => {
    if (isEligibleForFreeTrial && !hasSeenOffer()) {
      setShowFreeTrialPopup(true);
    }
  }, [isEligibleForFreeTrial]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissFreeTrialPopup = () => {
    markOfferSeen();
    setShowFreeTrialPopup(false);
  };

  const goToFreeTrial = () => {
    markOfferSeen();
    setShowFreeTrialPopup(false);
    navigate("/enduser/free-trial");
  };

  if (!showFreeTrialPopup) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="free-trial-popup-title"
      onClick={dismissFreeTrialPopup}
    >
      <div
        className="relative max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={dismissFreeTrialPopup}
          className="absolute top-3 left-3 text-gray-400 hover:text-gray-600"
          aria-label="סגירת חלון ההצעה"
        >
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="text-6xl mb-4" aria-hidden="true">
          🎟️
        </div>
        <h2
          id="free-trial-popup-title"
          className="text-2xl font-bold text-gray-900 mb-2"
        >
          קבלו {subscriptionStatus?.free_trial_max ?? 2} חישובים בחינם!
        </h2>
        <p className="text-gray-600 mb-6">
          לפני שמתחייבים למנוי - נסו את CarTax בחינם. הפעלת הניסיון אורכת דקה,
          בעזרת קוד אימות קצר שיישלח אליכם.
        </p>

        <button
          type="button"
          onClick={goToFreeTrial}
          className={`w-full inline-flex items-center justify-center px-5 py-3 text-base font-medium rounded-md text-white bg-green-600 ${getHoverClass(
            "hover:bg-green-700 transition-colors"
          )}`}
        >
          אני רוצה להתחיל ניסיון חינם
        </button>
        <button
          type="button"
          onClick={dismissFreeTrialPopup}
          className="mt-3 w-full text-sm text-gray-500 hover:text-gray-700 underline"
        >
          אולי מאוחר יותר
        </button>
      </div>
    </div>
  );
};

export default FreeTrialOfferPopup;
